import React from 'react';
import { useI18n } from '@whizz/react-i18n';
import type { ClassReportType, HighlightedProps, ProgressConsistencyGroupType } from '../../types';
import { progress_consistency_groups } from '../../types';
import { ProgressConsistencyGroup } from '../../../components';
import { SectionLayout } from '../modules';
import { APP_THEME } from '../../../styles/theme/styled/theme';
import { repository } from '@whizz/api-client';

const groups_colors: Record<ProgressConsistencyGroupType, string> = {
  occasional: APP_THEME.colors.rookie,
  intermittent: APP_THEME.colors.apprentice,
  steady: APP_THEME.colors.pro,
  reliable: APP_THEME.colors.master,
  constant: APP_THEME.colors.superstar,
};

type Props = {
  classReport: ClassReportType
  showOthersData: boolean
  hideAverage?: boolean
  chartIsExpanded?: boolean
  diffWeeks: number
} & HighlightedProps;

const ProgressConsistency = (
  {
    classReport,
    showOthersData,
    highlightedLabel,
    highlightedIds,
    hideAverage,
    chartIsExpanded,
    diffWeeks,
  }: Props,
): JSX.Element => {
  const { translate } = useI18n();
  let students = [ ...classReport.students.all ];

  if (!showOthersData) {
    students = students.filter((student) => highlightedIds?.includes(student.id));
  }

  const getHighlightedAvg = (): number | null => {
    if (highlightedIds && highlightedIds.length > 0) {
      const highlightedStudents = students.filter((student) => highlightedIds?.includes(student.id));

      return highlightedStudents.reduce((acc, student) => {
        return acc + (student.consistency ?? 0);
      }, 0) / Math.max(highlightedStudents.length, 1);
    }

    return null;
  };

  const highlightedAvg = getHighlightedAvg();

  return (
    <SectionLayout
      title={translate('pages.REPORTS.SECTIONS.PROGRESS_CONSISTENCY.title')}
      value={classReport.consistencyAverageName}
      highlightLabel={highlightedLabel}
      highlightValue={null != highlightedAvg
        ? repository.GroupReportMetricsRepository.getConsistencyName(highlightedAvg, diffWeeks)
        : null
      }
      moreInfo={translate('pages.REPORTS.SECTIONS.PROGRESS_CONSISTENCY.info')}
      className={'pdf-no-break'}
      data-cy="progress-consistency"
      hideAverage={hideAverage}
      chartIsExpanded={chartIsExpanded}
      id='progress-consistency-container'
    >
      <div
        className={'md:grid grid-cols-5 grid-flow-row'}
        data-cy='progress-consistency-chart'
      >
        {progress_consistency_groups.map((group) => (
          <ProgressConsistencyGroup
            key={`progress_consistency_${group}`}
            headline={translate(`pages.REPORTS.PROGRESS_CONSISTENCY.${group}`)}
            background={groups_colors[group]}
            students={students.filter((student) => student.consistencyName === group)}
            highlightedIds={highlightedIds}
          />
        ))}
      </div>
    </SectionLayout>
  );
};

ProgressConsistency.displayName = 'ProgressConsistency';

export default ProgressConsistency;
